"use client";

import { useEffect, useRef, useState } from "react";
import { Clock, FileText } from "lucide-react";
import { Progress } from "@/components/ui/progress";
export default function RevenueServices() {
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [progressValue, setProgressValue] = useState(0);
  const steps = [{
    number: "01",
    title: "REVENUE AUDIT",
    description: "We begin with a comprehensive diagnostic of your property's current performance. Our team analyzes historical data, market positioning, and competitor pricing to identify untapped revenue potential. We translate these findings into a strategic roadmap that anchors every subsequent optimization effort.",
    duration: "1 WEEK",
    deliverable: "PERFORMANCE AUDIT",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_audit.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_audit.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_audit.webp-wvc-sizes",
    alt: "Consultant reviewing hotel revenue data on a dashboard"
  }, {
    number: "02",
    title: "OTA STRATEGY",
    description: "We optimize your presence across all Online Travel Agencies. By refining content, managing inventory distribution, and leveraging algorithmic insights, we ensure your property captures maximum visibility and conversion. This phase builds the foundation for consistent, high-margin bookings.",
    duration: "2 WEEKS",
    deliverable: "OTA OPTIMIZATION PLAN",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/ota_strategy.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/ota_strategy.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/ota_strategy.webp-wvc-sizes",
    alt: "Digital interface showing OTA booking distribution metrics"
  }, {
    number: "03",
    title: "TECH INTEGRATION",
    description: "Modern hospitality requires a seamless tech stack. We recommend and implement advanced revenue management systems (RMS) and channel managers tailored to your property size. We ensure your systems communicate perfectly to automate pricing and inventory updates in real-time.",
    duration: "3 WEEKS",
    deliverable: "TECH STACK DEPLOYMENT",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/tech_integration.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/tech_integration.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/tech_integration.webp-wvc-sizes",
    alt: "Hotel management software dashboard on a laptop"
  }, {
    number: "04",
    title: "DYNAMIC PRICING",
    description: "We deploy data-driven pricing models that react to market demand, seasonality, and local events. By adjusting rates dynamically, we ensure your property remains competitive while maximizing RevPAR. Each pricing strategy is verified against your specific occupancy goals.",
    duration: "ONGOING",
    deliverable: "PRICING ENGINE",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/dynamic_pricing.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/dynamic_pricing.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/dynamic_pricing.png-wvc-sizes",
    alt: "Graph showing dynamic hotel room rate fluctuations"
  }, {
    number: "05",
    title: "GROWTH MONITORING",
    description: "The final phase is continuous performance tracking. We provide regular reporting, fine-tune strategies based on live market feedback, and offer direct access to your revenue lead. We ensure your property maintains its competitive edge and long-term profitability.",
    duration: "MONTHLY",
    deliverable: "GROWTH REPORT",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/growth_monitoring.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/growth_monitoring.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/growth_monitoring.png-wvc-sizes",
    alt: "Consultant presenting revenue growth charts to a hotel owner"
  }];
  useEffect(() => {
    const section = sectionRef.current;
    const track = trackRef.current;
    if (!section || !track) return;
    const handleScroll = () => {
      const rect = section.getBoundingClientRect();
      const sectionHeight = section.offsetHeight;
      const viewportHeight = window.innerHeight;
      const scrollableDistance = sectionHeight - viewportHeight;
      if (rect.top <= 0 && rect.bottom >= viewportHeight) {
        const scrolled = Math.abs(rect.top);
        const progress = Math.min(Math.max(scrolled / scrollableDistance, 0), 1);
        const totalTranslate = track.scrollWidth - window.innerWidth;
        track.style.transform = `translate3d(-${progress * totalTranslate}px, 0, 0)`;
        const stepIndex = Math.min(Math.floor(progress * steps.length), steps.length - 1);
        setActiveStep(stepIndex);
        setProgressValue(progress * 100);
      } else if (rect.top > 0) {
        track.style.transform = `translate3d(0, 0, 0)`;
        setActiveStep(0);
        setProgressValue(0);
      } else if (rect.bottom < viewportHeight) {
        const totalTranslate = track.scrollWidth - window.innerWidth;
        track.style.transform = `translate3d(-${totalTranslate}px, 0, 0)`;
        setActiveStep(steps.length - 1);
        setProgressValue(100);
      }
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    window.addEventListener("resize", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [steps.length]);
  return <section data-section-id="3064" id="remoterevenueservices" className="bg-background text-foreground">
      <div className="lg:hidden px-6 py-24 sm:py-32">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <p className="font-default text-xs sm:text-sm tracking-widest mb-8 text-secondary">
            // OUR PROCESS
          </p>
          <h2 className="font-default font-bold text-4xl sm:text-5xl md:text-6xl leading-[0.95] tracking-tight mb-8">
            Five steps to{" "}
            <span className="font-default italic font-normal">
              maximum revenue.
            </span>
          </h2>
          <p className="font-default text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-xl mx-auto">
            A proven revenue management protocol designed to optimize your hotel's performance. Each phase is built to drive direct bookings and increase your bottom line through data-driven strategies.
          </p>
        </div>

        <div className="space-y-12">
          {steps.map((step, i) => <div key={step.number} data-index={i} className="relative overflow-hidden">
              <div className="absolute -top-8 -left-2 font-default font-bold leading-none pointer-events-none select-none text-foreground/10" style={{
            fontSize: "clamp(8rem, 30vw, 16rem)"
          }}>
                {step.number}
              </div>

              <div className="relative backdrop-blur-sm bg-white border border-border p-6 sm:p-8 shadow-md rounded-lg">
                <img src={step.image} data-wvc-srcset={step.srcset} data-wvc-sizes={step.sizes} alt={step.alt} loading="lazy" decoding="async" className="w-full h-48 sm:h-56 object-cover mb-6 rounded-lg" />
                <h3 className="font-default font-bold text-2xl sm:text-3xl tracking-tight mb-4">
                  {step.title}
                </h3>
                <p className="font-default text-base sm:text-lg text-muted-foreground leading-relaxed mb-6">
                  {step.description}
                </p>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-6 pt-4 border-t border-border">
                  <div className="flex items-center gap-2 font-default text-xs tracking-widest">
                    <Clock className="w-4 h-4 text-secondary" />
                    <span className="text-muted-foreground">TIMELINE:</span>
                    <span>{step.duration}</span>
                  </div>
                  <div className="flex items-center gap-2 font-default text-xs tracking-widest">
                    <FileText className="w-4 h-4 text-secondary" />
                    <span className="text-muted-foreground">DELIVERABLE:</span>
                    <span>{step.deliverable}</span>
                  </div>
                </div>
              </div>
            </div>)}
        </div>
      </div>

      <div ref={sectionRef} className="hidden lg:block relative" style={{
      height: `${steps.length * 100}vh`
    }}>
        <div className="sticky top-0 h-screen w-full overflow-hidden">
          <div ref={trackRef} className="absolute top-0 left-0 h-full flex will-change-transform" style={{
          width: `${(steps.length + 1) * 100}vw`
        }}>
            <div className="w-screen h-screen flex items-center justify-center px-12 xl:px-24">
              <div className="max-w-3xl text-center">
                <p className="font-default text-sm tracking-widest mb-10 text-secondary">
                  // OUR PROCESS
                </p>
                <h2 className="font-default font-bold text-7xl xl:text-[6rem] leading-[0.9] tracking-tight mb-10">
                  Five steps to{" "}
                  <span className="font-default italic font-normal">
                    maximum revenue.
                  </span>
                </h2>
                <p className="font-default text-xl xl:text-2xl text-muted-foreground leading-relaxed max-w-[600px] mx-auto">
                  A proven revenue management protocol designed to optimize your hotel's performance. Each phase is built to drive direct bookings and increase your bottom line through data-driven strategies.
                </p>
              </div>
            </div>

            {steps.map((step, i) => <div key={step.number} data-index={i} className="w-screen h-screen relative flex items-center px-12 xl:px-24 overflow-hidden">
                <div className="absolute left-0 top-1/2 -translate-y-1/2 font-default font-bold leading-none pointer-events-none select-none text-foreground/10 -ml-4" style={{
              fontSize: "20rem"
            }}>
                  {step.number}
                </div>

                <div className="relative ml-auto w-full max-w-2xl xl:max-w-3xl backdrop-blur-sm bg-white border border-border p-10 xl:p-12 shadow-lg rounded-lg">
                  <div className="flex gap-8 mb-8">
                    <div className="flex-1">
                      <p className="font-default text-xs tracking-widest text-secondary mb-4">
                        STEP {step.number} / 05
                      </p>
                      <h3 className="font-default font-bold text-4xl xl:text-[2.5rem] leading-tight tracking-tight mb-6">
                        {step.title}
                      </h3>
                    </div>
                    <img src={step.image} data-wvc-srcset={step.srcset} data-wvc-sizes={step.sizes} alt={step.alt} loading="lazy" decoding="async" className="w-32 h-32 xl:w-40 xl:h-40 object-cover flex-shrink-0 rounded-lg" />
                  </div>
                  <p className="font-default text-lg xl:text-xl text-muted-foreground leading-relaxed mb-8">
                    {step.description}
                  </p>
                  <div className="flex flex-wrap gap-6 pt-6 border-t border-border">
                    <div className="flex items-center gap-2 font-default text-xs tracking-widest">
                      <Clock className="w-4 h-4 text-secondary" />
                      <span className="text-muted-foreground">TIMELINE:</span>
                      <span>{step.duration}</span>
                    </div>
                    <div className="flex items-center gap-2 font-default text-xs tracking-widest">
                      <FileText className="w-4 h-4 text-secondary" />
                      <span className="text-muted-foreground">
                        DELIVERABLE:
                      </span>
                      <span>{step.deliverable}</span>
                    </div>
                  </div>
                </div>
              </div>)}
          </div>

          <div className="absolute bottom-0 left-0 right-0 px-12 xl:px-24 pb-8 pointer-events-none">
            <div className="flex items-center gap-6 mb-4">
              <span className="font-default text-xs tracking-widest text-muted-foreground">
                PROGRESS
              </span>
              <span className="font-default text-xs tracking-widest text-secondary">
                {String(Math.min(activeStep + 1, steps.length)).padStart(2, "0")}{" "}
                / {String(steps.length).padStart(2, "0")}
              </span>
              <span className="font-default text-xs tracking-widest text-muted-foreground ml-auto">
                {activeStep >= 0 && activeStep < steps.length ? steps[activeStep].title : ""}
              </span>
            </div>
            <div className="grid grid-cols-5 gap-2">
              {steps.map((step, i) => <div key={step.number} data-index={i} className="relative h-[2px] bg-border overflow-hidden rounded-lg">
                  <div className="absolute inset-y-0 left-0 transition-all duration-300" style={{
                width: i < activeStep ? "100%" : i === activeStep ? `${(progressValue - i / steps.length * 100) / (100 / steps.length) * 100}%` : "0%",
                backgroundColor: "var(--secondary)"
              }} />
                </div>)}
            </div>
            <div className="mt-4">
              <Progress value={progressValue} className="h-[1px] bg-border rounded-lg sr-only" />
            </div>
          </div>
        </div>
      </div>
    </section>;
}